import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AgentConfig } from '../../models';
import { firstValueFrom } from 'rxjs';
import { LoggerService } from '../../../../services/logger-services/logger.service';

/**
 * Agent Configuration Service
 * 
 * Manages loading and caching of agent configurations from agent_config.json
 * 
 * Features:
 * - Lazy loading of agent config
 * - Prevents duplicate simultaneous loads
 * - Caches loaded configurations
 * - Provides methods to query agents by ID or filter criteria
 */
@Injectable({
    providedIn: 'root'
})
export class AgentConfigService {
    private readonly agents: Map<string, AgentConfig> = new Map();
    private apiConfig: any = null; // Store API configuration
    private apiEndpoints: any = null; // Store API endpoints configuration
    private mockResponses: any = null; // Store mock responses
    private loaded: boolean = false;
    private loadingPromise: Promise<void> | null = null;

    constructor(
        private readonly http: HttpClient,
        private readonly logger: LoggerService
    ) { }

    /**
     * Load ALL agent configurations at app startup
     * 
     * Loads:
     * - agent_config.json (agents + API config)
     * - api_endpoints.json (endpoint paths)
     * - api_mock_response.json (mock data)
     * 
     * Implements promise caching to prevent duplicate loads
     * 
     * @returns Promise<void>
     */
    async loadAgentConfig(): Promise<void> {
        try {
            if (this.loaded) {
                this.logger.logInfo('[AgentConfig] Using cached configuration', 'loadAgentConfig');
                return Promise.resolve();
            }
            
            if (this.loadingPromise) {
                this.logger.logInfo('[AgentConfig] Configuration load in progress, waiting...', 'loadAgentConfig');
                return this.loadingPromise;
            }

            this.loadingPromise = this.doLoad();
            return this.loadingPromise;
        } catch (error: any) {
            this.logger.logInfo('[AgentConfig] Error in loadAgentConfig', 'loadAgentConfig', error);
            throw error;
        }
    }

    /**
     * Internal method to perform the actual loading of ALL configurations
     * Loads all config files at once during app initialization
     * 
     * @returns Promise<void>
     */
    private async doLoad(): Promise<void> {
        try {
            this.logger.logInfo('[AgentConfig] 🔄 Loading ALL configurations...', 'doLoad');
            
            // 1. Load main agent configuration
            const config = await firstValueFrom(
                this.http.get<any>('config/agent_config.json')
            );
            
            // Store API configuration
            this.apiConfig = config.api || null;
            this.logger.logInfo(`[AgentConfig] ✓ API config loaded - useMock: ${this.apiConfig?.useMock}`, 'doLoad');
            
            // Load agents
            if (config.agents && Array.isArray(config.agents)) {
                this.agents.clear();
                
                config.agents.forEach((agentData: any) => {
                    const agent: AgentConfig = {
                        id: agentData.id,
                        name: agentData.name,
                        display_name: agentData.display_name,
                        type: agentData.type,
                        enabled: agentData.enabled ?? true,
                        privilege_required: agentData.privilege_required,
                        icon: agentData.icon,
                        sse_url: agentData.sse_url,
                        save_endpoint: agentData.save_endpoint,
                        // Include popup details
                        version: agentData.version,
                        about: agentData.about,
                        versionNotes: agentData.versionNotes,
                        features: agentData.features,
                        // Category for agent hub filtering
                        category: agentData.category,
                        // Thread pagination page size (defaults to 20 if not set)
                        thread_page_size: agentData.thread_page_size,
                        // Agent names used as the save_agent_response fallback when the stream omits them
                        agents: agentData.agents,
                        // Per-agent UI options (feedback, copy, audio, regenerate, share) – must be copied so Via/all-false hides buttons
                        options: agentData.options,
                        // Per-agent branding toggle: false forces design-system blue fallback.
                        branding_enabled: agentData.branding_enabled,
                        module_name: agentData.module_name,
                        module_display_name: agentData.module_display_name,
                        landing_page: agentData.landing_page || {
                            title: agentData.name || 'Agent',
                            intro: ''
                        },
                        prompt_library: agentData.prompt_library || [],
                    };
                    this.agents.set(agent.id, agent);
                });
                
                this.logger.logInfo(`[AgentConfig] ✓ Loaded ${this.agents.size} agents`, 'doLoad');
            }
            
            // 2. Load API endpoints configuration
            try {
                this.apiEndpoints = await firstValueFrom(
                    this.http.get<any>('/app/agents/config/api_endpoints.json')
                );
                this.logger.logInfo('[AgentConfig] ✓ api_endpoints.json loaded', 'doLoad');
            } catch (error) {
                this.logger.logInfo('[AgentConfig] ⚠️ api_endpoints.json not found, using defaults', 'doLoad');
                this.apiEndpoints = null;
            }
            
            // 3. Load mock responses configuration (only if useMock is true)
            if (this.apiConfig?.useMock) {
                try {
                    this.mockResponses = await firstValueFrom(
                        this.http.get<any>('/app/agents/config/api_mock_response.json')
                    );
                    const threadCount = this.mockResponses?.responses?.get_agent_threads?.data?.threads?.length || 0;
                    this.logger.logInfo(`[AgentConfig] ✓ api_mock_response.json loaded - ${threadCount} mock threads`, 'doLoad');
                } catch (error) {
                    this.logger.logInfo('[AgentConfig] ⚠️ api_mock_response.json not found', 'doLoad');
                    this.mockResponses = null;
                }
            } else {
                this.logger.logInfo('[AgentConfig] ⊗ Skipping mock data (useMock: false)', 'doLoad');
            }
            
            this.loaded = true;
            this.loadingPromise = null;
            this.logger.logInfo('[AgentConfig] ✅ ALL configurations loaded successfully!', 'doLoad');
        } catch (error: any) {
            this.loadingPromise = null;
            this.logger.logInfo('[AgentConfig] ❌ Error loading configurations', 'doLoad', error);
            this.loaded = false;
            throw error;
        }
    }

    /**
     * Get agent configuration by ID
     * 
     * @param id - Agent ID
     * @returns AgentConfig | undefined
     */
    public getAgentConfig(id: string): AgentConfig | undefined {
        try {
            return this.agents.get(id);
        } catch (error: any) {
            this.logger.logInfo('[AgentConfig] Error getting agent config', 'getAgentConfig', error);
            return undefined;
        }
    }

    /**
     * Whether API-driven branding applies to a specific agent.
     * Hub/module/workbench ids have no config and always use default theme (no API).
     */
    public isBrandingEnabledForAgent(agentId: string): boolean {
        const agent = this.getAgentConfig(agentId);
        if (!agent) {
            return false;
        }
        return agent.branding_enabled !== false;
    }

    /**
     * True when at least one of the given agents has branding enabled.
     * Used to skip the brand-colors API when every agent has branding disabled.
     */
    public hasBrandingEnabledAmongAgents(agentIds: string[]): boolean {
        return agentIds.some((id) => this.isBrandingEnabledForAgent(id));
    }

    /**
     * Get all loaded agent configurations
     * 
     * @returns AgentConfig[] - Array of all agents
     */
    public getAllAgents(): AgentConfig[] {
        try {
            return Array.from(this.agents.values());
        } catch (error: any) {
            this.logger.logInfo('[AgentConfig] Error getting all agents', 'getAllAgents', error);
            return [];
        }
    }

    /**
     * Get only enabled agents
     * 
     * @returns AgentConfig[] - Array of enabled agents
     */
    public getEnabledAgents(): AgentConfig[] {
        try {
            return this.getAllAgents().filter((agent: AgentConfig) => agent.enabled);
        } catch (error: any) {
            this.logger.logInfo('[AgentConfig] Error getting enabled agents', 'getEnabledAgents', error);
            return [];
        }
    }

    /**
     * Get API configuration
     * 
     * @returns API config object or null if not loaded
     */
    public getApiConfig(): any {
        try {
            return this.apiConfig;
        } catch (error: any) {
            this.logger.logInfo('[AgentConfig] Error getting API config', 'getApiConfig', error);
            return null;
        }
    }

    /**
     * Get API endpoints configuration
     * 
     * @returns Endpoints config object or null if not loaded
     */
    public getApiEndpoints(): any {
        try {
            return this.apiEndpoints;
        } catch (error: any) {
            this.logger.logInfo('[AgentConfig] Error getting API endpoints', 'getApiEndpoints', error);
            return null;
        }
    }

    /**
     * Get mock responses configuration
     * 
     * @returns Mock responses object or null if not loaded
     */
    public getMockResponses(): any {
        try {
            return this.mockResponses;
        } catch (error: any) {
            this.logger.logInfo('[AgentConfig] Error getting mock responses', 'getMockResponses', error);
            return null;
        }
    }

    /**
     * Check if configuration is loaded
     * 
     * @returns boolean
     */
    public isLoaded(): boolean {
        return this.loaded;
    }
}

